import React, { useState, useEffect, useRef } from 'react';
import { X, Send, User, Building2, Clock, CheckCircle, MessageCircle } from 'lucide-react';
import { connectToChat, sendMessage, getConversations, getMessages, markAsRead, createSupportConversation } from '../../services/chatService';
import Toast from '../Toast/Toast';
import './ChatSupportModal.scss';

const ChatSupportModal = ({ userId, userType, onClose }) => {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isReopening, setIsReopening] = useState(false);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  // ─── Inicializar/Carregar conversa existente ──────────────────────────────────
  useEffect(() => {
    const initConversation = async () => {
      try {
        setLoading(true);

        // Busca conversas existentes
        const data = await getConversations('support');
        let existingConv = null;

        if (data && data.length > 0) {
          // Busca conversa ativa (não resolvida) primeiro
          existingConv = data.find(conv =>
            (conv.userType === 'admin' || conv.title === 'Suporte' || conv.userId === userId) &&
            conv.status !== 3 // IGNORA CONVERSAS RESOLVIDAS
          );

          // Se não encontrar ativa, procura qualquer uma (inclusive resolvida)
          if (!existingConv && data.length > 0) {
            existingConv = data[0];
          }
        }

        if (existingConv) {
          // ✅ TEM CONVERSA
          // Verifica se está resolvida
          const isResolved = existingConv.status === 3;
          
          setConversation(existingConv);
          const messagesData = await getMessages(existingConv.id);
          setMessages(messagesData.messages || []);
          
          // Se estiver resolvida, mostra uma mensagem informativa
          if (isResolved) {
            showToast('Esta conversa foi resolvida. Envie uma nova mensagem para reabri-la.', 'info');
          }
        } else {
          // ❌ NÃO TEM CONVERSA - NÃO CRIA AGORA!
          setConversation(null);
          setMessages([]);
        }

        scrollToBottom();
      } catch (error) {
        console.error('Erro ao carregar conversa:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      initConversation();
    }
  }, [userId]);

  // ─── Conectar WebSocket ──────────────────────────────────────────────
  useEffect(() => {
    if (userId && conversation) {
      socketRef.current = connectToChat(userId);

      if (socketRef.current) {
        // Handler para new_message
        socketRef.current.on('new_message', (messageDto) => {
          if (conversation?.id === messageDto.conversationId) {
            setMessages(prev => {
              const exists = prev.some(m => m.id === messageDto.id);
              if (exists) return prev;
              return [...prev, messageDto];
            });
            scrollToBottom();
          }
        });

        // Handler para message_sent
        socketRef.current.on('message_sent', (messageDto) => {
          if (conversation?.id === messageDto.conversationId) {
            setMessages(prev => {
              const exists = prev.some(m => m.id === messageDto.id);
              if (exists) return prev;
              return [...prev, messageDto];
            });
            scrollToBottom();
          }
        });
      }

      return () => {
        if (socketRef.current) {
          socketRef.current.off('new_message');
          socketRef.current.off('message_sent');
        }
      };
    }
  }, [userId, conversation]);

  // ─── Função para criar conversa e enviar mensagem ───────────────────
  const createAndSendMessage = async (messageText) => {
    try {
      setIsCreatingConversation(true);
      
      // 1. Cria a conversa
      const result = await createSupportConversation(userId);
      
      if (!result || !result.id) {
        throw new Error('Erro ao criar conversa');
      }
      
      // 2. Define a conversa no estado
      setConversation(result);
      
      // 3. Envia a mensagem
      await sendMessage(result.id, messageText);
      
      // 4. Recarrega as mensagens
      const messagesData = await getMessages(result.id);
      setMessages(messagesData.messages || []);
      
      setDraft('');
      scrollToBottom();
      
      showToast('Mensagem enviada com sucesso!', 'success');
      
      return true;
    } catch (error) {
      console.error('Erro ao criar conversa e enviar mensagem:', error);
      showToast('Erro ao enviar mensagem. Tente novamente.', 'error');
      return false;
    } finally {
      setIsCreatingConversation(false);
    }
  };

  // ─── Função para reabrir conversa resolvida ─────────────────────────
  const reopenAndSendMessage = async (messageText) => {
    if (!conversation) return false;
    
    try {
      setIsReopening(true);
      
      // 1. Reabre a conversa (chama o endpoint de reabrir)
      await adminService.reopenConversation(conversation.id);
      
      // 2. Atualiza o status local
      setConversation(prev => ({ ...prev, status: 1 }));
      
      // 3. Envia a mensagem
      await sendMessage(conversation.id, messageText);
      
      // 4. Recarrega as mensagens
      const messagesData = await getMessages(conversation.id);
      setMessages(messagesData.messages || []);
      
      setDraft('');
      scrollToBottom();
      
      showToast('Conversa reaberta! Mensagem enviada.', 'success');
      
      return true;
    } catch (error) {
      console.error('Erro ao reabrir conversa:', error);
      showToast('Erro ao reabrir conversa. Tente novamente.', 'error');
      return false;
    } finally {
      setIsReopening(false);
    }
  };

  // ─── Enviar mensagem ──────────────────────────────────────────────────
  const handleSend = async () => {
    const messageText = draft.trim();
    
    if (!messageText || sending || isCreatingConversation || isReopening) return;

    setSending(true);

    try {
      // ✅ VERIFICA SE JÁ TEM CONVERSA
      if (conversation) {
        // ✅ TEM CONVERSA
        if (conversation.status === 3) {
          // ⚠️ CONVERSA RESOLVIDA - REABRE E ENVIA
          await reopenAndSendMessage(messageText);
        } else {
          // ✅ CONVERSA ATIVA - ENVIA NORMAL
          await sendMessage(conversation.id, messageText);
          setDraft('');
        }
      } else {
        // ❌ NÃO TEM CONVERSA - CRIA E ENVIA
        await createAndSendMessage(messageText);
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      showToast('Erro ao enviar mensagem. Tente novamente.', 'error');
    } finally {
      setSending(false);
    }
  };

  // ─── Scroll para o final ─────────────────────────────────────────────
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // ─── Verifica se a conversa está resolvida ──────────────────────────
  const isResolved = conversation?.status === 3;

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="chat-support-modal">
      <div className="chat-support-modal__overlay" onClick={onClose} />

      <div className="chat-support-modal__container">
        {/* Header */}
        <div className="chat-support-modal__header">
          <div className="chat-support-modal__title">
            <MessageCircle size={20} />
            <span>Suporte</span>
            <span className="chat-support-modal__status">
              <span className="chat-support-modal__status-dot" />
              Normalmente responde em minutos
            </span>
          </div>
          <button className="chat-support-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="chat-support-modal__body">
          <div className="chat-support-modal__main chat-support-modal__main--full">
            {loading ? (
              <div className="chat-support-modal__loading">
                <div className="chat-support-modal__spinner" />
                <span>Carregando conversa...</span>
              </div>
            ) : (
              <>
                <div className="chat-support-modal__conv-header">
                  <div className="chat-support-modal__conv-header-info">
                    <div className="chat-support-modal__conv-header-avatar">
                      S
                    </div>
                    <div>
                      <div className="chat-support-modal__conv-header-name">
                        Suporte
                        {isResolved && (
                          <span className="chat-support-modal__status-badge chat-support-modal__status-badge--resolved">
                            Resolvido
                          </span>
                        )}
                        <span className="chat-support-modal__conv-header-status">
                          <span className={`chat-support-modal__status-dot ${isResolved ? 'chat-support-modal__status-dot--offline' : 'chat-support-modal__status-dot--online'}`} />
                          {isResolved ? 'Aguardando reabertura' : 'Online'}
                        </span>
                      </div>
                      <div className="chat-support-modal__conv-header-type">
                        <Clock size={14} />
                        {isResolved 
                          ? 'Esta conversa foi resolvida. Envie uma mensagem para reabrir.'
                          : 'Resposta em até 24h'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="chat-support-modal__messages">
                  {messages.length === 0 ? (
                    <div className="chat-support-modal__messages-empty">
                      <MessageCircle size={48} />
                      <h4>Olá! Como podemos te ajudar?</h4>
                      <p>
                        {conversation && isResolved
                          ? "Esta conversa foi resolvida. Envie uma nova mensagem para reabri-la."
                          : conversation
                          ? "Envie sua mensagem que nossa equipe responderá em breve."
                          : "Digite sua mensagem abaixo para iniciar uma conversa com nossa equipe."}
                      </p>
                    </div>
                  ) : (
                    <>
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`chat-support-modal__message ${msg.senderId === userId
                            ? 'chat-support-modal__message--own'
                            : 'chat-support-modal__message--other'
                            }`}
                        >
                          {msg.senderId !== userId && (
                            <div className="chat-support-modal__msg-avatar">
                              {msg.senderName?.charAt(0).toUpperCase() || 'S'}
                            </div>
                          )}
                          <div className="chat-support-modal__msg-content">
                            <div className="chat-support-modal__msg-text">
                              {msg.message}
                            </div>
                            <div className="chat-support-modal__msg-time">
                              {new Date(msg.createdAt).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                <div className="chat-support-modal__input">
                  <input
                    type="text"
                    placeholder={
                      isCreatingConversation 
                        ? "Criando conversa..." 
                        : isReopening
                        ? "Reabrindo conversa..."
                        : isResolved
                        ? "Envie uma mensagem para reabrir..."
                        : "Digite sua mensagem..."
                    }
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                    disabled={sending || isCreatingConversation || isReopening}
                  />
                  <button
                    className={`chat-support-modal__send-btn ${isResolved ? 'chat-support-modal__send-btn--reopen' : ''}`}
                    onClick={handleSend}
                    disabled={
                      !draft.trim() || 
                      sending || 
                      isCreatingConversation ||
                      isReopening
                    }
                  >
                    {sending || isCreatingConversation || isReopening ? (
                      <div className="chat-support-modal__spinner-small" />
                    ) : isResolved ? (
                      'Reabrir'
                    ) : (
                      <Send size={18} />
                    )}
                  </button>
                </div>

                {/* Banner informativo para conversa resolvida */}
                {isResolved && (
                  <div className="chat-support-modal__resolved-banner">
                    <CheckCircle size={16} />
                    <span>Esta conversa foi resolvida. Envie uma nova mensagem para reabri-la.</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      </div>
    </div>
  );
};

export default ChatSupportModal;